import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useCreateEmployeeTimeOff } from '../../../services/react-query/employee_time_offs/useCreateEmployeeTimeOff';
import { useDestroyEmployeeTimeOff } from '../../../services/react-query/employee_time_offs/useDestroyEmployeeTimeOff';
import { useGetEmployeeTimeOffById } from '../../../services/react-query/employee_time_offs/useGetEmployeeTimeOffById';
import { useUpdateEmployeeTimeOff } from '../../../services/react-query/employee_time_offs/useUpdateEmployeeTimeOff';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { EmployeeTimeOff, EmployeeTimeOffMode } from '../../../types/EmployeeTimeOff';
import { Loading } from '../../../uikit';
import { humanizeDate } from '../../../utility/dateUtils';
import { errorPopulator } from '../../../utility/inputUtils';
import {
  baseErrorStyles,
  contentContainerStyles,
  dialogActionsStyles,
  dialogHeaderStyles,
  infoBoxStyles,
  infoTextStyles,
  weekdayBoxStyles,
  weekdaysContainerStyles,
  weekdaysTitleStyles,
} from '../../dashboard/AvailabilityPanel/availabilityPanelStyles';
import AvailabilityDeleteConfirmation from './AvailabilityDeleteConfirmation';

export const WEEKDAYS = [
  { value: 1, short: 'Mo' },
  { value: 2, short: 'Tu' },
  { value: 3, short: 'We' },
  { value: 4, short: 'Th' },
  { value: 5, short: 'Fr' },
  { value: 6, short: 'Sa' },
  { value: 0, short: 'Su' },
];

interface TimeOffModalProps {
  open: boolean;
  uuid?: string;
  employeeUuid: string;
  onClose: () => void;
  selectedDate?: string;
  mode: EmployeeTimeOffMode;
}

export default function TimeOffModal({ open, uuid, selectedDate, employeeUuid, mode, onClose }: TimeOffModalProps) {
  const [employeeTimeOff, setEmployeeTimeOff] = useState<EmployeeTimeOff | null>(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const isNew = uuid === 'new';
  const isWeekly = mode === EmployeeTimeOffMode.WEEKLY;

  const { data: employeeTimeOffData, isFetching: isFetchingEmployeeTimeOff } = useGetEmployeeTimeOffById({
    uuid: _.toString(uuid),
    enabled: open && !!uuid,
  });

  const { mutate: createTimeOff, isPending: isCreating } = useCreateEmployeeTimeOff({
    onSuccessCallback: onClose,
  });

  const { mutate: updateTimeOff, isPending: isUpdating } = useUpdateEmployeeTimeOff({
    uuid: _.toString(employeeTimeOff?.uuid),
    onSuccessCallback: onClose,
  });

  const { mutate: destroyTimeOff, isPending: isDestroying } = useDestroyEmployeeTimeOff({
    uuid: _.toString(employeeTimeOff?.uuid),
    onSuccessCallback: onClose,
  });

  const isPending = isCreating || isUpdating || isDestroying;
  useEffect(() => {
    if (!open) {
      setEmployeeTimeOff(null);

      return;
    }

    if (employeeTimeOffData) {
      const startDate = employeeTimeOffData.start_date || selectedDate || DateTime.now().toISODate();
      const endDate = employeeTimeOffData.end_date || DateTime.fromISO(startDate).toISODate() || '';

      setEmployeeTimeOff({
        ...employeeTimeOffData,
        employee_uuid: employeeTimeOffData.employee_uuid || employeeUuid,
        start_date: startDate,
        end_date: endDate,
        mode,
        // approved: isNew && canManageAll ? true : employeeTimeOffData.approved, // LATER TODO: Request Off Logic
      });

      return;
    }
  }, [open, employeeTimeOffData, selectedDate, employeeUuid, mode]);

  const submitDisabled = useMemo(() => {
    // Prevent submission while loading or before data is initialized.
    if (!employeeTimeOff || isPending) {
      return true;
    }

    // Both dates are required.
    if (!employeeTimeOff.start_date || !employeeTimeOff.end_date) {
      return true;
    }

    // Weekly schedules require at least one selected weekday.
    if (isWeekly && _.isEmpty(employeeTimeOff.details)) {
      return true;
    }

    return false;
  }, [employeeTimeOff, isPending, isWeekly]);

  const toggleWeekday = (weekday: number) => {
    if (!employeeTimeOff) {
      return;
    }
    const existing = employeeTimeOff.details || [];
    const details = _.includes(existing, weekday) ? _.without(existing, weekday) : _.sortBy([...existing, weekday]);
    setEmployeeTimeOff({
      ...employeeTimeOff,
      details,
    });
  };

  const handleSave = () => {
    if (!employeeTimeOff) {
      return;
    }
    if (isNew) {
      createTimeOff(employeeTimeOff);
      return;
    }
    updateTimeOff(employeeTimeOff);
  };

  const handleDelete = () => {
    if (!employeeTimeOff?.uuid) {
      return;
    }

    destroyTimeOff();
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <Box sx={dialogHeaderStyles}>
          <Typography {...headerTypographyProps}>
            {isWeekly ? (isNew ? 'Create Schedule' : 'Update Schedule') : isNew ? 'Request Time Off' : 'Update Request'}
          </Typography>

          <IconButton size="small" onClick={onClose} disabled={isPending}>
            <CloseIcon />
          </IconButton>
        </Box>

        {isFetchingEmployeeTimeOff || !employeeTimeOff ? (
          <DialogContent>
            <Loading />
          </DialogContent>
        ) : (
          <>
            <DialogContent>
              <Box sx={contentContainerStyles}>
                <TextField
                  label="From"
                  type="date"
                  value={employeeTimeOff.start_date}
                  {...errorPopulator(employeeTimeOff, 'start_date')}
                  onChange={(event) =>
                    setEmployeeTimeOff({
                      ...employeeTimeOff,
                      start_date: event.target.value,
                    })
                  }
                />

                <TextField
                  label="To"
                  type="date"
                  value={employeeTimeOff.end_date}
                  {...errorPopulator(employeeTimeOff, 'end_date')}
                  onChange={(event) =>
                    setEmployeeTimeOff({
                      ...employeeTimeOff,
                      end_date: event.target.value,
                    })
                  }
                />

                {isWeekly && (
                  <>
                    <Typography sx={weekdaysTitleStyles}>Weekdays</Typography>
                    <Box sx={weekdaysContainerStyles}>
                      {_.map(WEEKDAYS, (day) => {
                        const selected = _.includes(employeeTimeOff.details || [], day.value);

                        return (
                          <Box key={day.value} onClick={() => toggleWeekday(day.value)} sx={weekdayBoxStyles(selected)}>
                            {day.short}
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                <Box sx={infoBoxStyles}>
                  <Typography sx={infoTextStyles}>
                    Mark {humanizeDate(employeeTimeOff.start_date)} - {humanizeDate(employeeTimeOff.end_date)} as unavailable.
                  </Typography>
                </Box>

                {!_.isEmpty(employeeTimeOff.errors?.base) && (
                  <Box sx={baseErrorStyles}>
                    {_.map(employeeTimeOff.errors?.base, (error, index) => (
                      <Typography key={index} variant="body2">
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
              <Box>
                {!isNew && (
                  <Button color="error" variant="contained" disabled={isPending} onClick={() => setDeleteConfirmationOpen(true)}>
                    Delete
                  </Button>
                )}
              </Box>

              <Button variant="contained" disabled={submitDisabled} onClick={handleSave}>
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {employeeTimeOff && (
        <AvailabilityDeleteConfirmation
          employeeTimeOff={employeeTimeOff}
          open={deleteConfirmationOpen}
          onCancel={() => setDeleteConfirmationOpen(false)}
          onConfirm={() => {
            setDeleteConfirmationOpen(false);
            handleDelete();
          }}
        />
      )}
    </>
  );
}
